import React, { useMemo, useState } from 'react';
import { getErrorMessage } from '@/utils/formatApiError';
import AppButton from './AppButton';
import { cx } from '@/ui/utils/cx';

export type AppErrorBannerProps = {
  message: string | unknown;
  onDismiss?: () => void;
  /** Show raw technical details when message was derived from an object */
  showDetails?: boolean;
  className?: string;
};

function formatDetails(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/** Never renders [object Object] — normalizes any error shape to readable text. */
export default function AppErrorBanner({
  message,
  onDismiss,
  showDetails = true,
  className,
}: AppErrorBannerProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const text = getErrorMessage(message);
  const hasObjectSource = message != null && typeof message === 'object' && !(message instanceof Error);
  const details = useMemo(
    () => (hasObjectSource ? formatDetails(message) : ''),
    [hasObjectSource, message],
  );

  return (
    <div className={cx('app-error-banner', className)} role="alert">
      <div className="app-error-banner__row">
        <span className="app-error-banner__message">{text}</span>
        {onDismiss ? (
          <AppButton variant="ghost" size="sm" onClick={onDismiss} aria-label="Dismiss error">
            Dismiss
          </AppButton>
        ) : null}
      </div>
      {showDetails && hasObjectSource && details && details !== text ? (
        <>
          <AppButton
            variant="subtle"
            size="sm"
            className="app-error-banner__details-toggle"
            onClick={() => setDetailsOpen((v) => !v)}
            aria-expanded={detailsOpen}
          >
            {detailsOpen ? 'Hide details' : 'Show details'}
          </AppButton>
          {detailsOpen ? <pre className="app-error-banner__details">{details}</pre> : null}
        </>
      ) : null}
    </div>
  );
}
